'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import HUD from './HUD';
import StartScreen from './StartScreen';
import PauseMenu from './PauseMenu';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Star {
  x: number;
  y: number;
  radius: number;
  speed: number;
  opacity: number;
  twinkleOffset: number;
}

interface Asteroid {
  id: string;
  x: number;
  y: number;
  radius: number;
  speed: number;
  rotationAngle: number;
  rotationSpeed: number;
  vx: number;
  color: string;
  craterCount: number;
  craterPositions: { cx: number; cy: number; cr: number }[];
}

interface BossAsteroid {
  id: string;
  x: number;
  y: number;
  radius: number;
  targetY: number;
  rotationAngle: number;
  rotationSpeed: number;
  vx: number;
  color: string;
  craterPositions: { cx: number; cy: number; cr: number }[];
  maxHp: number;
  hp: number;
  hitFlash: number; // frames of white flash remaining
  phase: 'entering' | 'fighting' | 'dying';
  shakeX: number;
  shakeY: number;
}

interface Fragment {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  rotationAngle: number;
  rotationSpeed: number;
  color: string;
  life: number;
}

interface Bullet {
  id: string;
  x: number;
  y: number;
  vy: number;
  vx?: number;
  targetId?: string; // ion homing target
}

interface EnemyBullet {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Particle {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  life: number;
  maxLife: number;
}

interface ScorePopup {
  id: string;
  x: number;
  y: number;
  vy: number;
  text: string;
  life: number;
}

interface Spaceship {
  x: number;
  y: number;
  width: number;
  height: number;
  targetX: number;
  engineFlicker: number;
  rotation: number;
}

type GameState = 'start' | 'playing' | 'paused' | 'dead' | 'transitioning' | 'boss' | 'level_complete';

// ─── Level Definitions ────────────────────────────────────────────────────────

interface LevelConfig {
  id: number;
  name: string;
  bgColors: [string, string, string]; // gradient stops
  nebulaColor: string;
  starTint: string;
  asteroidColors: string[];
  bossColor: string;
  bossHp: number;
  bossRadius: number;
  spawnInterval: number;
  scrollSpeed: number;
  label: string;
}

const LEVELS: LevelConfig[] = [
  {
    id: 1,
    name: 'ASTEROID BELT',
    label: 'BANE 1',
    bgColors: ['#02040a', '#04080f', '#060c18'],
    nebulaColor: '20, 0, 60',
    starTint: '200, 220, 255',
    asteroidColors: ['#6b5e52', '#7a6b5a', '#8c7b6a', '#5a5248', '#9e8e7a', '#4a4540', '#b8a898', '#6e6258'],
    bossColor: '#7a6b5a',
    bossHp: 12,
    bossRadius: 90,
    spawnInterval: 55,
    scrollSpeed: 3,
    label: 'BANE 1',
  },
  {
    id: 2,
    name: 'LAVA FIELDS',
    label: 'BANE 2',
    bgColors: ['#0a0200', '#180600', '#200a00'],
    nebulaColor: '80, 10, 0',
    starTint: '255, 180, 100',
    asteroidColors: ['#8b2500', '#a33000', '#7a1a00', '#c04000', '#6b1500', '#b03500', '#5a1000', '#d04500'],
    bossColor: '#a33000',
    bossHp: 18,
    bossRadius: 100,
    spawnInterval: 42,
    scrollSpeed: 5,
    label: 'BANE 2',
  },
  {
    id: 3,
    name: 'DARK NEBULA',
    label: 'BANE 3',
    bgColors: ['#000008', '#00000f', '#020018'],
    nebulaColor: '60, 0, 120',
    starTint: '180, 100, 255',
    asteroidColors: ['#2a1a4a', '#3a2060', '#1a1030', '#4a2a70', '#251545', '#5a3080', '#150c25', '#6a3a90'],
    bossColor: '#4a2a70',
    bossHp: 25,
    bossRadius: 115,
    spawnInterval: 34,
    scrollSpeed: 7,
    label: 'BANE 3',
  },
  {
    id: 4,
    name: 'ICE FIELDS',
    label: 'BANE 4',
    bgColors: ['#000a12', '#001525', '#001e30'],
    nebulaColor: '0, 80, 140',
    starTint: '150, 220, 255',
    asteroidColors: ['#a8d8ea', '#7ec8e3', '#5ab5d4', '#3a9fc0', '#c8e8f5', '#8ecfe8', '#4a90b0', '#d5eef8'],
    bossColor: '#5ab5d4',
    bossHp: 32,
    bossRadius: 120,
    spawnInterval: 28,
    scrollSpeed: 8,
    label: 'BANE 4',
  },
  {
    id: 5,
    name: 'TOXIC STORM',
    label: 'BANE 5',
    bgColors: ['#020a00', '#041500', '#061c00'],
    nebulaColor: '40, 100, 0',
    starTint: '150, 255, 100',
    asteroidColors: ['#2a6b00', '#3a8500', '#1a5000', '#4a9a00', '#990000', '#ff3300', '#800000', '#ff4400'],
    bossColor: '#3a8500',
    bossHp: 40,
    bossRadius: 125,
    spawnInterval: 24,
    scrollSpeed: 9,
    label: 'BANE 5',
  },
  {
    id: 6,
    name: 'SOLAR FLARE',
    label: 'BANE 6',
    bgColors: ['#120500', '#200800', '#2a0a00'],
    nebulaColor: '180, 60, 0',
    starTint: '255, 200, 50',
    asteroidColors: ['#cc5500', '#e06000', '#b04000', '#f07000', '#a03500', '#d86500', '#903000', '#ff7500'],
    bossColor: '#e06000',
    bossHp: 50,
    bossRadius: 130,
    spawnInterval: 20,
    scrollSpeed: 10,
    label: 'BANE 6',
  },
  {
    id: 7,
    name: 'VOID SECTOR',
    label: 'BANE 7',
    bgColors: ['#000000', '#020002', '#040008'],
    nebulaColor: '30, 0, 80',
    starTint: '200, 150, 255',
    asteroidColors: ['#1a0030', '#2a0050', '#0f0020', '#3a0070', '#120025', '#450085', '#0a0018', '#500090'],
    bossColor: '#3a0070',
    bossHp: 60,
    bossRadius: 135,
    spawnInterval: 17,
    scrollSpeed: 11,
    label: 'BANE 7',
  },
  {
    id: 8,
    name: 'CRYSTAL CAVE',
    label: 'BANE 8',
    bgColors: ['#000810', '#001020', '#001530'],
    nebulaColor: '0, 120, 180',
    starTint: '100, 255, 255',
    asteroidColors: ['#00aacc', '#00c8e8', '#0090b0', '#00e0ff', '#007a99', '#00d5f0', '#006080', '#00f0ff'],
    bossColor: '#00c8e8',
    bossHp: 72,
    bossRadius: 140,
    spawnInterval: 14,
    scrollSpeed: 12,
    label: 'BANE 8',
  },
  {
    id: 9,
    name: 'INFERNO CORE',
    label: 'BANE 9',
    bgColors: ['#150000', '#250000', '#300000'],
    nebulaColor: '200, 0, 0',
    starTint: '255, 100, 50',
    asteroidColors: ['#cc0000', '#e50000', '#b00000', '#ff1a00', '#990000', '#ff3300', '#800000', '#ff4400'],
    bossColor: '#e50000',
    bossHp: 85,
    bossRadius: 145,
    spawnInterval: 11,
    scrollSpeed: 13,
    label: 'BANE 9',
  },
  {
    id: 10,
    name: 'GALACTIC CORE',
    label: 'BANE 10',
    bgColors: ['#050010', '#0a0020', '#0f0030'],
    nebulaColor: '100, 0, 200',
    starTint: '255, 255, 100',
    asteroidColors: ['#8800ff', '#aa00ff', '#6600cc', '#cc00ff', '#5500aa', '#dd00ff', '#4400aa', '#ff00ff'],
    bossColor: '#aa00ff',
    bossHp: 100,
    bossRadius: 155,
    spawnInterval: 8,
    scrollSpeed: 14,
    label: 'BANE 10',
  },
];

const TOTAL_LEVELS = LEVELS.length;
// Frames to play in each level before boss spawns
const LEVEL_DURATION_FRAMES = 1800; // ~30 seconds at 60fps

// ─── Trophy Definitions ───────────────────────────────────────────────────────

interface Trophy {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

const TROPHIES: Trophy[] = [
  { id: 'first_blood', name: 'FØRSTE BLOD', description: 'Ødelæg din første asteroide', icon: '🏆', color: '#cd7f32' },
  { id: 'level_2', name: 'BANE 2 KLAR', description: 'Gennemfør bane 1', icon: '🥉', color: '#cd7f32' },
  { id: 'level_3', name: 'BANE 3 KLAR', description: 'Gennemfør bane 2', icon: '🥉', color: '#cd7f32' },
  { id: 'level_5', name: 'HALVVEJS', description: 'Gennemfør bane 5', icon: '🥈', color: '#c0c0c0' },
  { id: 'level_8', name: 'NÆSTEN DER', description: 'Gennemfør bane 8', icon: '🥇', color: '#ffd700' },
  { id: 'level_10', name: 'GALAKTISK MESTER', description: 'Gennemfør alle 10 baner!', icon: '🏆', color: '#ffd700' },
  { id: 'score_1000', name: 'TUSIND POINT', description: 'Opnå 1.000 point', icon: '⭐', color: '#ffd700' },
  { id: 'score_5000', name: 'FEM TUSIND', description: 'Opnå 5.000 point', icon: '🌟', color: '#ffd700' },
  { id: 'score_10000', name: 'TI TUSIND', description: 'Opnå 10.000 point', icon: '💫', color: '#ffd700' },
  { id: 'boss_slayer', name: 'BOSS DRÆBER', description: 'Besejr din første boss', icon: '⚔️', color: '#ff6600' },
  { id: 'speed_demon', name: 'FARTDJÆVEL', description: 'Nå WARP 8 hastighed', icon: '🚀', color: '#00e5ff' },
  { id: 'survivor', name: 'OVERLEVER', description: 'Overlev 3 minutter', icon: '🛡️', color: '#00ff88' },
];

function getEarnedTrophies(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem('spaceracer-trophies') || '[]');
  } catch { return []; }
}

function awardTrophy(trophyId: string): boolean {
  const earned = getEarnedTrophies();
  if (earned.includes(trophyId)) return false;
  earned.push(trophyId);
  localStorage.setItem('spaceracer-trophies', JSON.stringify(earned));
  return true;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CANVAS_WIDTH = typeof window !== 'undefined' ? window.innerWidth : 1280;
const CANVAS_HEIGHT = typeof window !== 'undefined' ? window.innerHeight : 720;

const SHIP_WIDTH = 36;
const SHIP_HEIGHT = 52;
const SHIP_Y_RATIO = 0.72;
const SHIP_SPEED = 6;
const SPEED_INCREMENT_INTERVAL = 600;
const BASE_SCROLL_SPEED = 3;
const MAX_SCROLL_SPEED = 14;
const SPEED_INCREMENT = 0.35;
const BULLET_SPEED = 14;
const BULLET_COOLDOWN = 4;
const FRAGMENT_COUNT = 8;
const STAR_LAYERS = [
  { count: 80, speedMultiplier: 0.08, radiusRange: [0.3, 0.8] },
  { count: 50, speedMultiplier: 0.2,  radiusRange: [0.6, 1.2] },
  { count: 25, speedMultiplier: 0.45, radiusRange: [1.0, 2.0] },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateCraters(count: number, radius: number) {
  const craters: { cx: number; cy: number; cr: number }[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * radius * 0.65;
    craters.push({
      cx: Math.cos(angle) * dist,
      cy: Math.sin(angle) * dist,
      cr: radius * (0.1 + Math.random() * 0.18),
    });
  }
  return craters;
}

function createAsteroid(canvasWidth: number, scrollSpeed: number, frameCount: number, colors: string[], centerBias = false): Asteroid {
  const radius = 18 + Math.random() * 32;
  let x: number;
  if (centerBias) {
    // Spawn in the middle third of the screen
    const centerStart = canvasWidth * 0.3;
    const centerEnd = canvasWidth * 0.7;
    x = centerStart + Math.random() * (centerEnd - centerStart);
  } else {
    x = radius + Math.random() * (canvasWidth - radius * 2);
  }
  const color = colors[Math.floor(Math.random() * colors.length)];
  const craterCount = 2 + Math.floor(Math.random() * 4);
  return {
    id: `asteroid-${frameCount}-${Math.floor(Math.random() * 9999)}`,
    x,
    y: -radius - 10,
    radius,
    speed: scrollSpeed * (0.85 + Math.random() * 0.6),
    rotationAngle: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.04,
    vx: (Math.random() - 0.5) * 1.2,
    color,
    craterCount,
    craterPositions: generateCraters(craterCount, radius),
  };
}

function createBossAsteroid(canvasWidth: number, level: LevelConfig): BossAsteroid {
  const radius = level.bossRadius;
  return {
    id: `boss-${Date.now()}`,
    x: canvasWidth / 2,
    y: -radius - 20,
    radius,
    targetY: 160,
    rotationAngle: 0,
    rotationSpeed: 0.008,
    vx: 0,
    color: level.bossColor,
    craterPositions: generateCraters(8, radius),
    maxHp: level.bossHp,
    hp: level.bossHp,
    hitFlash: 0,
    phase: 'entering',
    shakeX: 0,
    shakeY: 0,
  };
}

function createFragments(asteroid: Asteroid | BossAsteroid, frameCount: number, count = FRAGMENT_COUNT): Fragment[] {
  const fragments: Fragment[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.6;
    const speed = 1.5 + Math.random() * 3.5;
    const fragRadius = asteroid.radius * (0.15 + Math.random() * 0.22);
    fragments.push({
      id: `frag-${frameCount}-${i}-${Math.floor(Math.random() * 9999)}`,
      x: asteroid.x + (Math.random() - 0.5) * asteroid.radius * 0.5,
      y: asteroid.y + (Math.random() - 0.5) * asteroid.radius * 0.5,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed + 1,
      radius: fragRadius,
      rotationAngle: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.12,
      color: asteroid.color,
      life: 1.0,
    });
  }
  return fragments;
}

function createBullet(ship: Spaceship, frameCount: number): Bullet {
  return {
    id: `bullet-${frameCount}-${Math.floor(Math.random() * 9999)}`,
    x: ship.x,
    y: ship.y - ship.height / 2 - 5,
    vy: -BULLET_SPEED,
  };
}

function createStars(canvasWidth: number, canvasHeight: number): Star[] {
  const stars: Star[] = [];
  STAR_LAYERS.forEach((layer) => {
    for (let i = 0; i < layer.count; i++) {
      stars.push({
        x: Math.random() * canvasWidth,
        y: Math.random() * canvasHeight,
        radius: layer.radiusRange[0] + Math.random() * (layer.radiusRange[1] - layer.radiusRange[0]),
        speed: BASE_SCROLL_SPEED * layer.speedMultiplier,
        opacity: 0.3 + Math.random() * 0.7,
        twinkleOffset: Math.random() * Math.PI * 2,
      });
    }
  });
  return stars;
}

function createExplosionParticles(x: number, y: number, frameCount: number, big = false): Particle[] {
  const particles: Particle[] = [];
  const colors = ['#ff1744', '#ff6d00', '#ffab00', '#ffffff', '#00e5ff', '#ff4081'];
  const count = big ? 80 : 40;
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.3;
    const speed = (big ? 2 : 1.5) + Math.random() * (big ? 8 : 5);
    particles.push({
      id: `particle-${frameCount}-${i}`,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: (big ? 2 : 1.5) + Math.random() * (big ? 5 : 3.5),
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1,
      maxLife: 0.6 + Math.random() * 0.4,
    });
  }
  return particles;
}

function createAsteroidHitParticles(x: number, y: number, color: string, frameCount: number): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 12; i++) {
    const angle = (Math.PI * 2 * i) / 12 + (Math.random() - 0.5) * 0.4;
    const speed = 1 + Math.random() * 3;
    particles.push({
      id: `hit-${frameCount}-${i}`,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: 1 + Math.random() * 2,
      color,
      life: 1,
      maxLife: 0.5 + Math.random() * 0.3,
    });
  }
  return particles;
}

function circlesOverlap(ax: number, ay: number, ar: number, bx: number, by: number, br: number): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  return Math.sqrt(dx * dx + dy * dy) < ar + br - 4;
}

// ─── Audio Engine ─────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;
let ambienceNode: AudioBufferSourceNode | null = null;
let ambienceGain: GainNode | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtx;
}

function playLaserSound() {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  } catch (_) {}
}

function playExplosionSound() {
  try {
    const ctx = getAudioContext();
    const sampleRate = ctx.sampleRate;
    const duration = 2.0;
    const bufferSize = Math.floor(sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);

    // Layered explosion: initial crack + long deep rumble + extended tail
    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      const noise = Math.random() * 2 - 1;
      // Sharp transient at start (crack)
      const crack = Math.exp(-t * 40) * 1.2;
      // Long mid rumble — slow decay for "prooooh"
      const rumble = Math.exp(-t * 2.5) * 1.1;
      // Deep low-frequency thump
      const thump = Math.sin(2 * Math.PI * 40 * t) * Math.exp(-t * 4) * 1.0;
      // Sub-bass punch — very deep and long
      const sub = Math.sin(2 * Math.PI * 20 * t) * Math.exp(-t * 1.8) * 0.9;
      // Extra deep body
      const body = Math.sin(2 * Math.PI * 55 * t) * Math.exp(-t * 3) * 0.7;
      data[i] = noise * (crack + rumble) + thump + sub + body;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // Low-pass to remove harsh high frequencies — deeper cutoff
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(1200, ctx.currentTime);
    lowpass.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + duration);

    // Compressor to punch it up
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 6;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.001;
    compressor.release.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(1.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    source.connect(lowpass);
    lowpass.connect(compressor);
    compressor.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);
  } catch (_) {}
}

function playAsteroidHitSound() {
  try {
    const ctx = getAudioContext();
    const bufferSize = ctx.sampleRate * 0.18;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2);
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.28, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);
  } catch (_) {}
}

function playBossHitSound() {
  try {
    const ctx = getAudioContext();
    const bufferSize = ctx.sampleRate * 0.25;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 1.2);
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.45, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(600, ctx.currentTime);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);
  } catch (_) {}
}

function playBossDeathSound() {
  try {
    const ctx = getAudioContext();
    // Multi-layered boom
    for (let wave = 0; wave < 3; wave++) {
      const delay = wave * 0.15;
      const bufferSize = ctx.sampleRate * 0.6;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 1.0);
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.7 - wave * 0.15, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.6);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(300 - wave * 60, ctx.currentTime + delay);
      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      source.start(ctx.currentTime + delay);
    }
  } catch (_) {}
}

function playLevelCompleteSound() {
  try {
    const ctx = getAudioContext();
    const notes = [523, 659, 784, 1047]; // C5 E5 G5 C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.start(t);
      osc.stop(t + 0.35);
    });
  } catch (_) {}
}

function startAmbience() {
  try {
    const ctx = getAudioContext();
    if (ambienceNode) return;
    ambienceGain = ctx.createGain();
    ambienceGain.gain.setValueAtTime(0, ctx.currentTime);
    ambienceGain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 3);
    ambienceGain.connect(ctx.destination);
    const freqs = [55, 82.5, 110, 165];
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      osc.type = i % 2 === 0 ? 'sine' : 'triangle';
      osc.frequency.value = freq;
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.05 + i * 0.03;
      lfoGain.gain.value = freq * 0.008;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start();
      oscGain.gain.value = 0.25 - i * 0.04;
      osc.connect(oscGain);
      oscGain.connect(ambienceGain!);
      osc.start();
    });
    const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseData.length; i++) noiseData[i] = Math.random() * 2 - 1;
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'lowpass';
    noiseFilter.frequency.value = 120;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.04;
    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ambienceGain);
    noiseSource.start();
  } catch (_) {}
}

function stopAmbience() {
  try {
    if (ambienceGain && audioCtx) {
      ambienceGain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 1.5);
      setTimeout(() => { ambienceNode = null; }, 2000);
    }
  } catch (_) {}
}

// ─── Drawing Functions ────────────────────────────────────────────────────────

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function drawSpaceship(ctx: CanvasRenderingContext2D, ship: Spaceship, frameCount: number, shipConfig?: { speedBonus: number; width: number; height: number; hullColor: string; accentColor: string }) {
  const { x, y, width, height } = ship;
  const hullBase = shipConfig?.hullColor ?? '#4a0a0a';
  const accent = shipConfig?.accentColor ?? '#cc2222';

  // Derive lighter/darker hull shades from hullBase
  const hullMid = hullBase.replace('#', '');
  const r0 = parseInt(hullMid.slice(0, 2), 16);
  const g0 = parseInt(hullMid.slice(2, 4), 16);
  const b0 = parseInt(hullMid.slice(4, 6), 16);
  const hullLight = `rgb(${Math.min(255, r0 + 60)},${Math.min(255, g0 + 60)},${Math.min(255, b0 + 60)})`;
  const hullDark = `rgb(${Math.max(0, r0 - 20)},${Math.max(0, g0 - 20)},${Math.max(0, b0 - 20)})`;
  const hullDarkRgba = (alpha: number) => `rgba(${Math.max(0, r0 - 20)},${Math.max(0, g0 - 20)},${Math.max(0, b0 - 20)},${alpha})`;

  ctx.save();
  ctx.translate(x, y);
  if (ship.rotation !== 0) {
    ctx.rotate(ship.rotation);
  }

  const engineFlicker = 0.7 + Math.sin(frameCount * 0.3) * 0.3;
  const engineGlow = ctx.createRadialGradient(0, height * 0.45, 0, 0, height * 0.45, 28 * engineFlicker);
  engineGlow.addColorStop(0, `rgba(255, 80, 0, ${0.9 * engineFlicker})`);
  engineGlow.addColorStop(0.4, `rgba(200, 30, 0, ${0.4 * engineFlicker})`);
  engineGlow.addColorStop(1, 'rgba(100, 0, 0, 0)');
  ctx.fillStyle = engineGlow;
  ctx.beginPath();
  ctx.ellipse(0, height * 0.45, 18 * engineFlicker, 24 * engineFlicker, 0, 0, Math.PI * 2);
  ctx.fill();

  for (let i = 0; i < 3; i++) {
    const trailAlpha = (0.6 - i * 0.18) * engineFlicker;
    const trailGlow = ctx.createRadialGradient(0, height * 0.42 + i * 10, 0, 0, height * 0.42 + i * 10, 8 - i * 1.5);
    trailGlow.addColorStop(0, `rgba(255, 150, 50, ${trailAlpha})`);
    trailGlow.addColorStop(1, 'rgba(150, 0, 0, 0)');
    ctx.fillStyle = trailGlow;
    ctx.beginPath();
    ctx.ellipse(0, height * 0.42 + i * 10, 5 - i * 0.8, 8 - i * 1.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.beginPath();
  ctx.moveTo(0, -height / 2);
  ctx.bezierCurveTo(width * 0.15, -height * 0.1, width * 0.5, height * 0.1, width * 0.45, height * 0.35);
  ctx.lineTo(0, height * 0.28);
  ctx.lineTo(-width * 0.45, height * 0.35);
  ctx.bezierCurveTo(-width * 0.5, height * 0.1, -width * 0.15, -height * 0.1, 0, -height / 2);
  ctx.closePath();

  const hullGrad = ctx.createLinearGradient(-width * 0.5, 0, width * 0.5, 0);
  hullGrad.addColorStop(0, hullDark);
  hullGrad.addColorStop(0.3, hullBase);
  hullGrad.addColorStop(0.5, hullLight);
  hullGrad.addColorStop(0.7, hullBase);
  hullGrad.addColorStop(1, hullDark);
  ctx.fillStyle = hullGrad;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(accent, 0.733);
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(width * 0.2, height * 0.05);
  ctx.lineTo(width * 0.45, height * 0.35);
  ctx.lineTo(width * 0.15, height * 0.15);
  ctx.closePath();
  const wingGradR = ctx.createLinearGradient(width * 0.2, 0, width * 0.45, 0);
  wingGradR.addColorStop(0, hullBase);
  wingGradR.addColorStop(1, hullDark);
  ctx.fillStyle = wingGradR;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(accent, 0.333);
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-width * 0.2, height * 0.05);
  ctx.lineTo(-width * 0.45, height * 0.35);
  ctx.lineTo(-width * 0.15, height * 0.15);
  ctx.closePath();
  const wingGradL = ctx.createLinearGradient(-width * 0.45, 0, -width * 0.2, 0);
  wingGradL.addColorStop(0, hullDark);
  wingGradL.addColorStop(1, hullBase);
  ctx.fillStyle = wingGradL;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(accent, 0.333);
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(0, -height * 0.15, width * 0.12, height * 0.18, 0, 0, Math.PI * 2);
  const cockpitGrad = ctx.createRadialGradient(-2, -height * 0.2, 1, 0, -height * 0.15, width * 0.12);
  cockpitGrad.addColorStop(0, 'rgba(255, 200, 180, 0.95)');
  cockpitGrad.addColorStop(0.4, hexToRgba(accent, 0.733));
  cockpitGrad.addColorStop(1, hullDarkRgba(0.4));
  ctx.fillStyle = cockpitGrad;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(accent, 0.933);
  ctx.lineWidth = 1;
  ctx.stroke();

  const lightPulse = 0.5 + Math.sin(frameCount * 0.15) * 0.5;
  ctx.beginPath();
  ctx.arc(width * 0.42, height * 0.28, 2.5, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(255, 255, 50, ${lightPulse})`;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-width * 0.42, height * 0.28, 2.5, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(255, 255, 50, ${lightPulse})`;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -height / 2);
  ctx.bezierCurveTo(width * 0.06, -height * 0.3, width * 0.08, -height * 0.1, 0, 0);
  ctx.strokeStyle = hexToRgba(accent, 0.467);
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();
}

function drawAsteroid(ctx: CanvasRenderingContext2D, asteroid: Asteroid) {
  ctx.save();
  ctx.translate(asteroid.x, asteroid.y);
  ctx.rotate(asteroid.rotationAngle);

  const r = asteroid.radius;
  ctx.beginPath();
  const points = 10;
  for (let i = 0; i < points; i++) {
    const angle = (Math.PI * 2 * i) / points;
    const variance = 0.75 + ((i * 7 + 3) % 5) * 0.1;
    const px = Math.cos(angle) * r * variance;
    const py = Math.sin(angle) * r * variance;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();

  const bodyGrad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, 0, 0, 0, r);
  bodyGrad.addColorStop(0, lightenColor(asteroid.color, 30));
  bodyGrad.addColorStop(0.5, asteroid.color);
  bodyGrad.addColorStop(1, darkenColor(asteroid.color, 40));
  ctx.fillStyle = bodyGrad;
  ctx.fill();
  ctx.strokeStyle = darkenColor(asteroid.color, 60);
  ctx.lineWidth = 1;
  ctx.stroke();

  asteroid.craterPositions.forEach((crater) => {
    ctx.beginPath();
    ctx.arc(crater.cx, crater.cy, crater.cr, 0, Math.PI * 2);
    ctx.fillStyle = darkenColor(asteroid.color, 50);
    ctx.fill();
    ctx.strokeStyle = darkenColor(asteroid.color, 70);
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(crater.cx - crater.cr * 0.3, crater.cy - crater.cr * 0.3, crater.cr * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,0.06)`;
    ctx.fill();
  });

  ctx.restore();
}

function drawAlienUFO(ctx: CanvasRenderingContext2D, boss: BossAsteroid, frameCount: number) {
  ctx.save();
  ctx.translate(boss.x + boss.shakeX, boss.y + boss.shakeY);

  const r = boss.radius;
  const hpRatio = boss.hp / boss.maxHp;
  const pulse = Math.sin(frameCount * 0.08) * 0.5 + 0.5;
  const hitFlashing = boss.hitFlash > 0;

  // Outer energy glow
  const glowAlpha = 0.12 + (1 - hpRatio) * 0.25 + pulse * 0.08;
  const outerGlow = ctx.createRadialGradient(0, 0, r * 0.5, 0, 0, r * 1.8);
  outerGlow.addColorStop(0, `rgba(0, 255, 120, ${glowAlpha})`);
  outerGlow.addColorStop(0.5, `rgba(0, 200, 80, ${glowAlpha * 0.4})`);
  outerGlow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = outerGlow;
  ctx.beginPath();
  ctx.arc(0, 0, r * 1.8, 0, Math.PI * 2);
  ctx.fill();

  // UFO saucer — bottom disc (wide flat rim)
  const rimW = r * 1.0;
  const rimH = r * 0.28;
  ctx.beginPath();
  ctx.ellipse(0, r * 0.05, rimW, rimH, 0, 0, Math.PI * 2);
  const rimGrad = ctx.createLinearGradient(0, -rimH, 0, rimH);
  if (hitFlashing) {
    rimGrad.addColorStop(0, `rgba(255,255,255,${boss.hitFlash / 8})`);
    rimGrad.addColorStop(1, '#aaffcc');
  } else {
    rimGrad.addColorStop(0, '#33cc77');
    rimGrad.addColorStop(0.5, '#1a8844');
    rimGrad.addColorStop(1, '#0d4422');
  }
  ctx.fillStyle = rimGrad;
  ctx.fill();
  ctx.strokeStyle = hitFlashing ? '#ffffff' : '#00ff88';
  ctx.lineWidth = 2;
  ctx.stroke();

  // UFO dome (top bubble)
  const domeR = r * 0.52;
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.12, domeR, domeR * 0.72, 0, Math.PI, 0);
  const domeGrad = ctx.createRadialGradient(-domeR * 0.25, -r * 0.35, 0, 0, -r * 0.12, domeR);
  if (hitFlashing) {
    domeGrad.addColorStop(0, 'rgba(255,255,255,0.9)');
    domeGrad.addColorStop(1, 'rgba(200,255,220,0.5)');
  } else {
    domeGrad.addColorStop(0, 'rgba(180, 255, 200, 0.85)');
    domeGrad.addColorStop(0.4, 'rgba(0, 200, 100, 0.6)');
    domeGrad.addColorStop(1, 'rgba(0, 80, 40, 0.4)');
  }
  ctx.fillStyle = domeGrad;
  ctx.fill();
  ctx.strokeStyle = hitFlashing ? '#ffffff' : 'rgba(0,255,140,0.7)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Dome shine highlight
  ctx.beginPath();
  ctx.ellipse(-domeR * 0.2, -r * 0.38, domeR * 0.22, domeR * 0.14, -0.4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.fill();

  // Alien face inside dome
  // Head (green alien)
  const headR = domeR * 0.38;
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.22, headR, headR * 1.15, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#44cc66';
  ctx.fill();

  // Big alien eyes
  const eyeOffX = headR * 0.38;
  const eyeOffY = -r * 0.26;
  const eyeRx = headR * 0.32;
  const eyeRy = headR * 0.42;
  // Left eye
  ctx.beginPath();
  ctx.ellipse(-eyeOffX, eyeOffY, eyeRx, eyeRy, -0.2, 0, Math.PI * 2);
  ctx.fillStyle = '#000000';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-eyeOffX - eyeRx * 0.2, eyeOffY - eyeRy * 0.25, eyeRx * 0.3, eyeRy * 0.3, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fill();
  // Right eye
  ctx.beginPath();
  ctx.ellipse(eyeOffX, eyeOffY, eyeRx, eyeRy, 0.2, 0, Math.PI * 2);
  ctx.fillStyle = '#000000';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(eyeOffX - eyeRx * 0.2, eyeOffY - eyeRy * 0.25, eyeRx * 0.3, eyeRy * 0.3, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fill();

  // Alien mouth (angry when low hp)
  ctx.beginPath();
  if (hpRatio < 0.4) {
    // Angry frown
    ctx.arc(0, -r * 0.08, headR * 0.28, 0.3, Math.PI - 0.3);
    ctx.strokeStyle = '#003311';
    ctx.lineWidth = 2;
    ctx.stroke();
  } else {
    // Neutral line
    ctx.moveTo(-headR * 0.22, -r * 0.08);
    ctx.lineTo(headR * 0.22, -r * 0.08);
    ctx.strokeStyle = '#003311';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Rotating lights on rim
  const lightCount = 8;
  for (let i = 0; i < lightCount; i++) {
    const angle = (Math.PI * 2 * i) / lightCount + frameCount * 0.06;
    const lx = Math.cos(angle) * rimW * 0.78;
    const ly = r * 0.05 + Math.sin(angle) * rimH * 0.4;
    const lightPulse = (Math.sin(frameCount * 0.15 + i * 0.8) + 1) * 0.5;
    const lightColors = ['#ff0044', '#ffaa00', '#00ffaa', '#0088ff', '#ff00ff', '#ffff00', '#00ffff', '#ff4400'];
    ctx.beginPath();
    ctx.arc(lx, ly, 4 + lightPulse * 2, 0, Math.PI * 2);
    ctx.fillStyle = lightColors[i % lightColors.length];
    ctx.shadowColor = lightColors[i % lightColors.length];
    ctx.shadowBlur = 8;
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  // Tractor beam (when hp < 60%)
  if (hpRatio < 0.6) {
    const beamAlpha = (0.6 - hpRatio) * 0.4 * (0.5 + pulse * 0.5);
    const beamGrad = ctx.createLinearGradient(0, rimH, 0, r * 1.4);
    beamGrad.addColorStop(0, `rgba(0, 255, 120, ${beamAlpha})`);
    beamGrad.addColorStop(1, `rgba(0, 255, 120, 0)`);
    ctx.beginPath();
    ctx.moveTo(-rimW * 0.3, rimH);
    ctx.lineTo(-rimW * 0.6, r * 1.4);
    ctx.lineTo(rimW * 0.6, r * 1.4);
    ctx.lineTo(rimW * 0.3, rimH);
    ctx.closePath();
    ctx.fillStyle = beamGrad;
    ctx.fill();
  }

  ctx.restore();
}

function drawEnemyBullet(ctx: CanvasRenderingContext2D, bullet: EnemyBullet) {
  ctx.save();
  const glow = ctx.createRadialGradient(bullet.x, bullet.y, 0, bullet.x, bullet.y, 10);
  glow.addColorStop(0, 'rgba(255,50,50,0.7)');
  glow.addColorStop(1, 'rgba(255,0,0,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(bullet.x, bullet.y, 10, 0, Math.PI * 2);
  ctx.fill();
  // Bullet body
  const angle = Math.atan2(bullet.vy, bullet.vx);
  ctx.translate(bullet.x, bullet.y);
  ctx.rotate(angle + Math.PI / 2);
  ctx.beginPath();
  ctx.rect(-2, -8, 4, 16);
  const bulletGrad = ctx.createLinearGradient(0, -8, 0, 8);
  bulletGrad.addColorStop(0, '#ffffff');
  bulletGrad.addColorStop(0.3, '#ff4444');
  bulletGrad.addColorStop(1, 'rgba(200,0,0,0)');
  ctx.fillStyle = bulletGrad;
  ctx.fill();
  ctx.restore();
}

function drawFragment(ctx: CanvasRenderingContext2D, frag: Fragment) {
  ctx.save();
  ctx.translate(frag.x, frag.y);
  ctx.rotate(frag.rotationAngle);
  ctx.globalAlpha = frag.life;

  const r = frag.radius;
  ctx.beginPath();
  const pts = 5 + Math.floor(r);
  for (let i = 0; i < pts; i++) {
    const angle = (Math.PI * 2 * i) / pts;
    const variance = 0.6 + ((i * 5 + 2) % 4) * 0.12;
    const px = Math.cos(angle) * r * variance;
    const py = Math.sin(angle) * r * variance;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = frag.color;
  ctx.fill();
  ctx.strokeStyle = darkenColor(frag.color, 40);
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawBullet(ctx: CanvasRenderingContext2D, bullet: Bullet, blasterColor = '#00ffe0', blasterGlow = 'rgba(0,255,224,0.6)') {
  ctx.save();
  const glow = ctx.createRadialGradient(bullet.x, bullet.y, 0, bullet.x, bullet.y, 10);
  glow.addColorStop(0, blasterGlow);
  glow.addColorStop(1, 'rgba(0,255,200,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(bullet.x, bullet.y, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.rect(bullet.x - 2, bullet.y - 8, 4, 16);
  const bulletGrad = ctx.createLinearGradient(bullet.x, bullet.y - 8, bullet.x, bullet.y + 8);
  bulletGrad.addColorStop(0, '#ffffff');
  bulletGrad.addColorStop(0.3, blasterColor);
  bulletGrad.addColorStop(1, 'rgba(0,200,150,0)');
  ctx.fillStyle = bulletGrad;
  ctx.fill();
  ctx.restore();
}

function drawStars(ctx: CanvasRenderingContext2D, stars: Star[], frameCount: number, tint = '200, 220, 255') {
  stars.forEach((star) => {
    const twinkle = 0.4 + Math.sin(frameCount * 0.02 + star.twinkleOffset) * 0.3;
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * twinkle})`;
    ctx.fill();
    if (star.radius > 1.2) {
      const glow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.radius * 3);
      glow.addColorStop(0, `rgba(${tint}, ${0.2 * twinkle})`);
      glow.addColorStop(1, `rgba(${tint}, 0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius * 3, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[]) {
  particles.forEach((p) => {
    const alpha = p.life;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(0, p.radius * p.life), 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  });
}

function drawNebula(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number, color: string) {
  const nebulaPositions = [
    { x: canvasWidth * 0.15, y: canvasHeight * 0.3, r: 220 },
    { x: canvasWidth * 0.8, y: canvasHeight * 0.6, r: 180 },
    { x: canvasWidth * 0.5, y: canvasHeight * 0.1, r: 150 },
  ];
  nebulaPositions.forEach((n) => {
    const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
    grad.addColorStop(0, `rgba(${color}, 0.08)`);
    grad.addColorStop(0.5, `rgba(${color}, 0.04)`);
    grad.addColorStop(1, `rgba(${color}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(n.x, n.y, n.r, n.r * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawSpeedLines(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number, scrollSpeed: number, frameCount: number) {
  if (scrollSpeed < 6) return;
  const intensity = Math.min((scrollSpeed - 6) / 8, 1);
  const lineCount = Math.floor(8 * intensity);
  ctx.save();
  for (let i = 0; i < lineCount; i++) {
    let x = (canvasWidth * (i + 0.5)) / lineCount + Math.sin(frameCount * 0.05 + i) * 30;
    const length = 40 + Math.random() * 80;
    const alpha = 0.03 + intensity * 0.06;
    ctx.strokeStyle = `rgba(0, 180, 255, ${alpha})`;
    ctx.lineWidth = 0.5 + Math.random();
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + (Math.random() - 0.5) * 10, length);
    ctx.stroke();
  }
  ctx.restore();
}

function drawScorePopups(ctx: CanvasRenderingContext2D, popups: ScorePopup[]) {
  popups.forEach((p) => {
    const alpha = Math.min(1, p.life * 2);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = 'bold 18px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffeb3b';
    ctx.shadowColor = '#ff9800';
    ctx.shadowBlur = 10;
    ctx.fillText(p.text, p.x, p.y);
    ctx.restore();
  });
}

// ─── Shop Upgrades ────────────────────────────────────────────────────────────

function getSelectedBlaster(): string {
  if (typeof window === 'undefined') return 'blaster_basic';
  return localStorage.getItem('spaceracer-blaster') || 'blaster_basic';
}

function getSelectedShip(): string {
  if (typeof window === 'undefined') return 'ship_viper';
  return localStorage.getItem('spaceracer-ship') || 'ship_viper';
}

function getBlasterConfig(blasterId: string): { cooldown: number; speed: number; double: boolean; color: string; glowColor: string } {
  switch (blasterId) {
    case 'blaster_plasma':
      return { cooldown: 3, speed: 13, double: false, color: '#ff6600', glowColor: 'rgba(255,102,0,0.6)' };
    case 'blaster_ion':
      return { cooldown: 5, speed: 14, double: true, color: '#ff00ff', glowColor: 'rgba(255,0,255,0.6)' };
    case 'blaster_quantum':
      return { cooldown: 2, speed: 16, double: false, color: '#ffff00', glowColor: 'rgba(255,255,0,0.8)' };
    default:
      return { cooldown: 4, speed: 14, double: false, color: '#00ffe0', glowColor: 'rgba(0,255,224,0.6)' };
  }
}

function getShipConfig(shipId: string): { speedBonus: number; width: number; height: number; hullColor: string; accentColor: string } {
  switch (shipId) {
    case 'ship_falcon':
      return { speedBonus: 2, width: 34, height: 50, hullColor: '#0a2a4a', accentColor: '#00aaff' };
    case 'ship_titan':
      return { speedBonus: 0, width: 44, height: 58, hullColor: '#0a2a1a', accentColor: '#00ff88' };
    case 'ship_phantom':
      return { speedBonus: 3, width: 36, height: 52, hullColor: '#1a0a2a', accentColor: '#cc00ff' };
    default:
      return { speedBonus: 0, width: 36, height: 52, hullColor: '#4a0a0a', accentColor: '#cc2222' };
  }
}

// Color helpers
function lightenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + amount);
  const b = Math.min(255, (num & 0xff) + amount);
  return `rgb(${r},${g},${b})`;
}

function darkenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `rgb(${r},${g},${b})`;
}

// ─── Planet Drawing ───────────────────────────────────────────────────────────

function getPlanetConfig(planetName: string): { color1: string; color2: string; ringColor: string | null; hasRing: boolean; cloudColor: string } {
  const name = (planetName || '').toLowerCase();
  if (name.includes('mars') || name.includes('rød') || name.includes('red')) {
    return { color1: '#c1440e', color2: '#8b2500', ringColor: null, hasRing: false, cloudColor: 'rgba(200,100,60,0.3)' };
  }
  if (name.includes('saturn') || name.includes('ring')) {
    return { color1: '#c8a96e', color2: '#a07840', ringColor: 'rgba(200,180,120,0.5)', hasRing: true, cloudColor: 'rgba(220,200,140,0.3)' };
  }
  if (name.includes('jupiter') || name.includes('stor') || name.includes('big')) {
    return { color1: '#c88b3a', color2: '#8b5a1a', ringColor: null, hasRing: false, cloudColor: 'rgba(200,140,80,0.4)' };
  }
  if (name.includes('ice') || name.includes('is') || name.includes('neptun') || name.includes('uranus')) {
    return { color1: '#4fc3f7', color2: '#0277bd', ringColor: 'rgba(100,200,255,0.3)', hasRing: true, cloudColor: 'rgba(150,220,255,0.3)' };
  }
  if (name.includes('lava') || name.includes('fire') || name.includes('ild')) {
    return { color1: '#ff5722', color2: '#b71c1c', ringColor: null, hasRing: false, cloudColor: 'rgba(255,100,50,0.4)' };
  }
  if (name.includes('crystal') || name.includes('krystal')) {
    return { color1: '#00e5ff', color2: '#006064', ringColor: 'rgba(0,229,255,0.4)', hasRing: true, cloudColor: 'rgba(0,200,220,0.3)' };
  }
  if (name.includes('void') || name.includes('dark') || name.includes('mørk')) {
    return { color1: '#4a148c', color2: '#1a0030', ringColor: null, hasRing: false, cloudColor: 'rgba(100,0,180,0.3)' };
  }
  // Default: blue/green earth-like
  return { color1: '#1565c0', color2: '#0d3b6e', ringColor: null, hasRing: false, cloudColor: 'rgba(100,180,255,0.3)' };
}

function drawFreeFlyPlanet(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number, planetName: string, frameCount: number) {
  const cfg = getPlanetConfig(planetName);
  ctx.save();

  // Outer glow
  const glowGrad = ctx.createRadialGradient(cx, cy, radius * 0.8, cx, cy, radius * 1.6);
  glowGrad.addColorStop(0, `rgba(${hexToRgbComponents(cfg.color1)}, 0.15)`);
  glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glowGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.6, 0, Math.PI * 2);
  ctx.fill();

  // Ring (behind planet)
  if (cfg.hasRing && cfg.ringColor) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, 0.28);
    ctx.beginPath();
    ctx.arc(0, 0, radius * 1.55, 0, Math.PI * 2);
    ctx.strokeStyle = cfg.ringColor;
    ctx.lineWidth = radius * 0.22;
    ctx.stroke();
    ctx.restore();
  }

  // Planet body
  const bodyGrad = ctx.createRadialGradient(cx - radius * 0.3, cy - radius * 0.3, 0, cx, cy, radius);
  bodyGrad.addColorStop(0, lightenColor(cfg.color1, 40));
  bodyGrad.addColorStop(0.5, cfg.color1);
  bodyGrad.addColorStop(1, cfg.color2);
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = bodyGrad;
  ctx.fill();

  // Surface bands / clouds
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();
  const bandOffset = (frameCount * 0.04) % (radius * 2);
  for (let i = 0; i < 5; i++) {
    const by = cy - radius + (i / 5) * radius * 2 + bandOffset * 0.3;
    const bh = radius * 0.18;
    ctx.beginPath();
    ctx.ellipse(cx, by, radius * 0.95, bh, 0, 0, Math.PI * 2);
    ctx.fillStyle = cfg.cloudColor;
    ctx.fill();
  }
  ctx.restore();

  // Atmosphere rim
  const atmGrad = ctx.createRadialGradient(cx, cy, radius * 0.85, cx, cy, radius * 1.05);
  atmGrad.addColorStop(0, 'rgba(255,255,255,0)');
  atmGrad.addColorStop(0.6, `rgba(${hexToRgbComponents(cfg.color1)}, 0.12)`);
  atmGrad.addColorStop(1, 'rgba(255,255,255,0.08)');
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.05, 0, Math.PI * 2);
  ctx.fillStyle = atmGrad;
  ctx.fill();

  // Shine highlight
  ctx.beginPath();
  ctx.ellipse(cx - radius * 0.28, cy - radius * 0.32, radius * 0.28, radius * 0.18, -0.5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.fill();

  ctx.restore();
}

function hexToRgbComponents(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function GameCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Planet/level from galaxy map
  const planetParam = searchParams?.get('planet');
  const levelParam = searchParams?.get('level');
  const startLevelIndex = levelParam ? Math.max(0, parseInt(levelParam, 10) - 1) : 0;
  const startFromPlanet = !!planetParam;

  // Game state refs
  const gameStateRef = useRef<GameState>('start');
  const frameCountRef = useRef(0);
  const scoreRef = useRef(0);
  const scrollSpeedRef = useRef(BASE_SCROLL_SPEED);
  const shipRef = useRef<Spaceship>({
    x: 0,
    y: 0,
    width: SHIP_WIDTH,
    height: SHIP_HEIGHT,
    targetX: 0,
    engineFlicker: 0,
    rotation: 0,
  });
  const starsRef = useRef<Star[]>([]);
  const asteroidsRef = useRef<Asteroid[]>([]);
  const fragmentsRef = useRef<Fragment[]>([]);
  const bulletsRef = useRef<Bullet[]>([]);
  const enemyBulletsRef = useRef<EnemyBullet[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const scorePopupsRef = useRef<ScorePopup[]>([]);
  const keysRef = useRef<Record<string, boolean>>({});
  const animFrameRef = useRef<number>(0);
  const spawnTimerRef = useRef(0);
  const speedTierRef = useRef(1);
  const canvasSizeRef = useRef({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const bulletCooldownRef = useRef(0);
  const audioStartedRef = useRef(false);

  // Free-fly orbit refs
  const orbitAngleRef = useRef(0);
  const orbitRadiusRef = useRef(0);

  // Level system refs
  const currentLevelRef = useRef(0); // index into LEVELS
  const levelFrameRef = useRef(0); // frames elapsed in current level
  const bossRef = useRef<BossAsteroid | null>(null);
  const bossActiveRef = useRef(false);
  const levelCompleteTimerRef = useRef(0);

  // Shop upgrade refs
  const selectedBlasterRef = useRef('blaster_basic');
  const selectedShipRef = useRef('ship_viper');

  // Trophy tracking refs
  const asteroidsDestroyedRef = useRef(0);
  const bossesDefeatedRef = useRef(0);
  const survivalFramesRef = useRef(0);

  // React state for HUD
  const [hudScore, setHudScore] = useState(0);
  const [hudSpeed, setHudSpeed] = useState(1);
  const [gamePhase, setGamePhase] = useState<GameState>(startFromPlanet ? 'playing' : 'start');
  const [hudLevel, setHudLevel] = useState(1);
  const [bossHp, setBossHp] = useState(0);
  const [bossMaxHp, setBossMaxHp] = useState(0);
  const [showBossWarning, setShowBossWarning] = useState(false);
  const [levelTransitionText, setLevelTransitionText] = useState('');

  // Trophy popup state
  const [trophyPopup, setTrophyPopup] = useState<{ trophy: Trophy; visible: boolean } | null>(null);
  const trophyQueueRef = useRef<Trophy[]>([]);
  const trophyShowingRef = useRef(false);

  // Pause menu state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const soundEnabledRef = useRef(true);
  const [difficulty, setDifficulty] = useState<'easy' | 'normal' | 'hard'>('normal');
  const difficultyRef = useRef<'easy' | 'normal' | 'hard'>('normal');

  const getDifficultyMultiplier = () => {
    if (difficultyRef.current === 'easy') return 0.7;
    if (difficultyRef.current === 'hard') return 1.4;
    return 1.0;
  };

  const getCurrentLevel = () => LEVELS[Math.min(hudLevel - 1, LEVELS.length - 1)];

  // Trophy award helper
  const tryAwardTrophy = useCallback((trophyId: string) => {
    const wasNew = awardTrophy(trophyId);
    if (!wasNew) return;
    const trophy = TROPHIES.find(t => t.id === trophyId);
    if (!trophy) return;
    trophyQueueRef.current.push(trophy);
    if (!trophyShowingRef.current) {
      showNextTrophy();
    }
  }, []); // eslint-disable-line

  const showNextTrophy = useCallback(() => {
    if (trophyQueueRef.current.length === 0) {
      trophyShowingRef.current = false;
      return;
    }
    trophyShowingRef.current = true;
    const next = trophyQueueRef.current.shift()!;
    setTrophyPopup({ trophy: next, visible: true });
    setTimeout(() => {
      setTrophyPopup(prev => prev ? { ...prev, visible: false } : null);
      setTimeout(() => {
        setTrophyPopup(null);
        showNextTrophy();
      }, 500);
    }, 3000);
  }, []); // eslint-disable-line

  // ── Init ──────────────────────────────────────────────────────────────────

  const initGame = useCallback((canvasWidth: number, canvasHeight: number) => {
    // Load shop selections
    selectedBlasterRef.current = getSelectedBlaster();
    selectedShipRef.current = getSelectedShip();
    const shipCfg = getShipConfig(selectedShipRef.current);

    shipRef.current = {
      x: canvasWidth / 2,
      y: canvasHeight * SHIP_Y_RATIO,
      width: shipCfg.width,
      height: shipCfg.height,
      targetX: canvasWidth / 2,
      engineFlicker: 0,
      rotation: 0,
    };
    starsRef.current = createStars(canvasWidth, canvasHeight);
    asteroidsRef.current = [];
    fragmentsRef.current = [];
    bulletsRef.current = [];
    enemyBulletsRef.current = [];
    particlesRef.current = [];
    scorePopupsRef.current = [];
    frameCountRef.current = 0;
    scoreRef.current = 0;
    scrollSpeedRef.current = BASE_SCROLL_SPEED;
    speedTierRef.current = 1;
    spawnTimerRef.current = 0;
    bulletCooldownRef.current = 0;
    currentLevelRef.current = startLevelIndex;
    levelFrameRef.current = 0;
    bossRef.current = null;
    bossActiveRef.current = false;
    levelCompleteTimerRef.current = 0;
    asteroidsDestroyedRef.current = 0;
    bossesDefeatedRef.current = 0;
    survivalFramesRef.current = 0;
    gameStateRef.current = 'playing';
    setGamePhase('playing');
    setHudScore(0);
    setHudSpeed(1);
    setHudLevel(startLevelIndex + 1);
    setBossHp(0);
    setBossMaxHp(0);
    setShowBossWarning(false);
    // Set orbit radius outside the planet for free-fly mode
    if (startFromPlanet) {
      const planetRadius = Math.min(canvasWidth, canvasHeight) * 0.13;
      orbitRadiusRef.current = planetRadius + Math.min(canvasWidth, canvasHeight) * 0.12;
      orbitAngleRef.current = -Math.PI / 2; // start at top of planet
    }
    if (soundEnabledRef.current) startAmbience();
  }, [startLevelIndex]);

  // ── Pause / Resume helpers ────────────────────────────────────────────────

  const pauseGame = useCallback(() => {
    if (gameStateRef.current !== 'playing' && gameStateRef.current !== 'boss') return;
    gameStateRef.current = 'paused';
    setGamePhase('paused');
    if (ambienceGain && audioCtx) {
      ambienceGain.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    }
  }, []);

  const resumeGame = useCallback(() => {
    if (gameStateRef.current !== 'paused') return;
    const prevState = bossActiveRef.current ? 'boss' : 'playing';
    gameStateRef.current = prevState;
    setGamePhase(prevState);
    if (soundEnabledRef.current && ambienceGain && audioCtx) {
      ambienceGain.gain.linearRampToValueAtTime(0.06, audioCtx.currentTime + 0.3);
    }
  }, []);

  // ── Game Loop ─────────────────────────────────────────────────────────────

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width: cw, height: ch } = canvasSizeRef.current;
    const state = gameStateRef.current;
    const level = getCurrentLevel();

    // ── Clear ──────────────────────────────────────────────────────────────
    ctx.clearRect(0, 0, cw, ch);

    // ── Background (level-themed) ──────────────────────────────────────────
    const bgGrad = ctx.createLinearGradient(0, 0, 0, ch);
    bgGrad.addColorStop(0, level.bgColors[0]);
    bgGrad.addColorStop(0.5, level.bgColors[1]);
    bgGrad.addColorStop(1, level.bgColors[2]);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, cw, ch);

    drawNebula(ctx, cw, ch, level.nebulaColor);
    drawSpeedLines(ctx, cw, ch, scrollSpeedRef.current, frameCountRef.current);

    // ── Stars ──────────────────────────────────────────────────────────────
    if (state === 'playing' || state === 'boss' || state === 'level_complete') {
      starsRef.current.forEach((star) => {
        const speedMultiplier = scrollSpeedRef.current / BASE_SCROLL_SPEED;
        star.y += star.speed * speedMultiplier;
        if (star.y > ch + star.radius) {
          star.y = -star.radius;
          star.x = Math.random() * cw;
        }
      });
    }
    drawStars(ctx, starsRef.current, frameCountRef.current, level.starTint);

    if (state === 'playing' || state === 'boss' || state === 'level_complete') {

      if (state === 'playing' || state === 'boss') {
        frameCountRef.current++;
        levelFrameRef.current++;
        survivalFramesRef.current++;

        const diffMult = getDifficultyMultiplier();

        // ── Speed progression ────────────────────────────────────────────────
        if (frameCountRef.current % SPEED_INCREMENT_INTERVAL === 0) {
          scrollSpeedRef.current = Math.min(
            scrollSpeedRef.current + SPEED_INCREMENT,
            MAX_SCROLL_SPEED
          );
          speedTierRef.current = Math.floor(
            ((scrollSpeedRef.current - BASE_SCROLL_SPEED) / (MAX_SCROLL_SPEED - BASE_SCROLL_SPEED)) * 9
          ) + 1;
          setHudSpeed(speedTierRef.current);
          // Trophy: speed demon
          if (speedTierRef.current >= 8) tryAwardTrophy('speed_demon');
        }

        // ── Score ─────────────────────────────────────────────────────────────
        scoreRef.current += Math.ceil(scrollSpeedRef.current * 0.5);
        if (frameCountRef.current % 6 === 0) {
          setHudScore(scoreRef.current);
          // Score trophies
          if (scoreRef.current >= 1000) tryAwardTrophy('score_1000');
          if (scoreRef.current >= 5000) tryAwardTrophy('score_5000');
          if (scoreRef.current >= 10000) tryAwardTrophy('score_10000');
        }

        // ── Survival trophy ───────────────────────────────────────────────────
        if (survivalFramesRef.current === 60 * 60 * 3) { // 3 minutes at 60fps
          tryAwardTrophy('survivor');
        }

        // ── Ship movement ─────────────────────────────────────────────────────
        const ship = shipRef.current;
        const halfW = ship.width / 2;
        const shipSpeedBonus = getShipConfig(selectedShipRef.current).speedBonus;
        const effectiveShipSpeed = SHIP_SPEED + shipSpeedBonus;

        if (startFromPlanet) {
          // Free-fly orbit mode: ship orbits the planet
          const orbitSpeed = 0.012 + (effectiveShipSpeed - SHIP_SPEED) * 0.001;
          if (keysRef.current['ArrowLeft'] || keysRef.current['a'] || keysRef.current['A']) {
            orbitAngleRef.current -= orbitSpeed;
          }
          if (keysRef.current['ArrowRight'] || keysRef.current['d'] || keysRef.current['D']) {
            orbitAngleRef.current += orbitSpeed;
          }
          // Auto-orbit slowly when no key pressed
          orbitAngleRef.current += 0.004;
          const planetCX = cw / 2;
          const planetCY = ch / 2;
          ship.x = planetCX + Math.cos(orbitAngleRef.current) * orbitRadiusRef.current;
          ship.y = planetCY + Math.sin(orbitAngleRef.current) * orbitRadiusRef.current;
          ship.targetX = ship.x;
          // Rotate ship to face the direction of travel (tangent to orbit)
          // The tangent direction is orbitAngle + PI/2 (perpendicular to radius, in direction of increasing angle)
          ship.rotation = orbitAngleRef.current + Math.PI / 2;
        } else {
          if (keysRef.current['ArrowLeft'] || keysRef.current['a'] || keysRef.current['A']) {
            ship.targetX = Math.max(halfW, ship.targetX - effectiveShipSpeed);
          }
          if (keysRef.current['ArrowRight'] || keysRef.current['d'] || keysRef.current['D']) {
            ship.targetX = Math.min(cw - halfW, ship.targetX + effectiveShipSpeed);
          }
          ship.x += (ship.targetX - ship.x) * 0.18;
          ship.rotation = 0;
        }

        // ── Shooting ──────────────────────────────────────────────────────────
        if (bulletCooldownRef.current > 0) bulletCooldownRef.current--;
        const blasterCfg = getBlasterConfig(selectedBlasterRef.current);
        if (keysRef.current[' '] && bulletCooldownRef.current === 0) {
          if (blasterCfg.double) {
            // Ion double-shot: each bullet seeks a different asteroid
            const visibleAsteroids = [...asteroidsRef.current].sort((a, b) => {
              const da = Math.hypot(a.x - ship.x, a.y - ship.y);
              const db = Math.hypot(b.x - ship.x, b.y - ship.y);
              return da - db;
            });
            const target1 = visibleAsteroids[0] ?? null;
            const target2 = visibleAsteroids[1] ?? null;

            const makeIonBullet = (idx: number, target: typeof target1, fallbackOffsetX: number): Bullet => {
              const bx = ship.x + fallbackOffsetX;
              const by = ship.y - ship.height / 2 - 5;
              if (target) {
                const dx = target.x - bx;
                const dy = target.y - by;
                const dist = Math.hypot(dx, dy) || 1;
                const speed = BULLET_SPEED;
                return {
                  id: `bullet-ion-${frameCountRef.current}-${idx}`,
                  x: bx,
                  y: by,
                  vx: (dx / dist) * speed,
                  vy: (dy / dist) * speed,
                  targetId: target.id,
                };
              }
              return {
                id: `bullet-ion-${frameCountRef.current}-${idx}`,
                x: bx,
                y: by,
                vx: 0,
                vy: -BULLET_SPEED,
              };
            };

            bulletsRef.current.push(makeIonBullet(0, target1, -7));
            bulletsRef.current.push(makeIonBullet(1, target2 ?? target1, 7));
          } else {
            bulletsRef.current.push(createBullet(ship, frameCountRef.current));
          }
          bulletCooldownRef.current = blasterCfg.cooldown;
          if (soundEnabledRef.current) playLaserSound();
        }

        // ── Move bullets ──────────────────────────────────────────────────────
        bulletsRef.current = bulletsRef.current.filter((b) => {
          // Homing: steer ion bullets toward their tracked target each frame
          if (b.targetId) {
            const allTargets = [...asteroidsRef.current, ...(bossRef.current ? [bossRef.current] : [])];
            const target = allTargets.find((a) => a.id === b.targetId);
            if (target) {
              const dx = target.x - b.x;
              const dy = target.y - b.y;
              const dist = Math.hypot(dx, dy) || 1;
              const speed = BULLET_SPEED;
              b.vx = (dx / dist) * speed;
              b.vy = (dy / dist) * speed;
            }
          }
          b.x += b.vx ?? 0;
          b.y += b.vy;
          return b.y > -20 && b.y < (canvasSizeRef.current.height + 20) && b.x > -20 && b.x < (canvasSizeRef.current.width + 20);
        });

        // ── Move enemy bullets ────────────────────────────────────────────────
        enemyBulletsRef.current = enemyBulletsRef.current.filter((b) => {
          b.x += b.vx;
          b.y += b.vy;
          return b.x > -20 && b.x < cw + 20 && b.y > -20 && b.y < ch + 20;
        });

        // ── Move asteroids ────────────────────────────────────────────────────
        if (state === 'playing') {
          asteroidsRef.current = asteroidsRef.current.filter((a) => {
            a.y += a.speed;
            a.x += a.vx;
            a.rotationAngle += a.rotationSpeed;
            return a.y < ch + a.radius + 20;
          });
        }

        // ── Spawn asteroids (normal mode only) ───────────────────────────────
        if (state === 'playing' && !startFromPlanet) {
          const diffMult2 = getDifficultyMultiplier();
          const effectiveInterval = Math.max(8, Math.floor(level.spawnInterval / diffMult2));
          spawnTimerRef.current++;
          if (spawnTimerRef.current >= effectiveInterval) {
            spawnTimerRef.current = 0;
            asteroidsRef.current.push(
              createAsteroid(cw, scrollSpeedRef.current, frameCountRef.current, level.asteroidColors)
            );
          }
        }

        // ── Bullet-asteroid collision ─────────────────────────────────────────
        if (state === 'playing') {
          const bulletsToRemove = new Set<string>();
          const asteroidsToRemove = new Set<string>();

          for (const bullet of bulletsRef.current) {
            for (const asteroid of asteroidsRef.current) {
              if (asteroidsToRemove.has(asteroid.id)) continue;
              if (circlesOverlap(bullet.x, bullet.y, 4, asteroid.x, asteroid.y, asteroid.radius * 0.82)) {
                bulletsToRemove.add(bullet.id);
                asteroidsToRemove.add(asteroid.id);

                // Explosion effects
                particlesRef.current.push(...createExplosionParticles(asteroid.x, asteroid.y, frameCountRef.current));
                fragmentsRef.current.push(...createFragments(asteroid, frameCountRef.current));
                if (soundEnabledRef.current) playExplosionSound();

                // Score
                const pts = Math.round(asteroid.radius * 2);
                scoreRef.current += pts;
                setHudScore(scoreRef.current);
                scorePopupsRef.current.push({
                  id: `popup-${frameCountRef.current}-${asteroid.id}`,
                  x: asteroid.x,
                  y: asteroid.y,
                  vy: -2,
                  text: `+${pts}`,
                  life: 1.0,
                });

                // Trophy: first asteroid
                asteroidsDestroyedRef.current++;
                if (asteroidsDestroyedRef.current === 1) tryAwardTrophy('first_blood');

                break;
              }
            }
          }

          if (bulletsToRemove.size > 0) {
            bulletsRef.current = bulletsRef.current.filter((b) => !bulletsToRemove.has(b.id));
          }
          if (asteroidsToRemove.size > 0) {
            asteroidsRef.current = asteroidsRef.current.filter((a) => !asteroidsToRemove.has(a.id));
          }
        }

        // ── Ship-asteroid collision ───────────────────────────────────────────
        if (state === 'playing') {
          const ship = shipRef.current;
          const shipCollisionRadius = ship.width * 0.38;
          for (const asteroid of asteroidsRef.current) {
            if (circlesOverlap(ship.x, ship.y - ship.height * 0.1, shipCollisionRadius, asteroid.x, asteroid.y, asteroid.radius * 0.82)) {
              particlesRef.current.push(...createExplosionParticles(ship.x, ship.y, frameCountRef.current));
              gameStateRef.current = 'dead';
              setGamePhase('dead');
              const finalScore = scoreRef.current;
              const stored = localStorage.getItem('spaceracer-highscore');
              const prev = stored ? parseInt(stored, 10) : 0;
              if (finalScore > prev) localStorage.setItem('spaceracer-highscore', String(finalScore));
              localStorage.setItem('spaceracer-lastscore', String(finalScore));
              localStorage.setItem('spaceracer-lastspeed', String(speedTierRef.current));
              // Save coins (1 coin per 10 points)
              const earnedCoins = Math.floor(finalScore / 10);
              const prevCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
              localStorage.setItem('spaceracer-coins', String(prevCoins + earnedCoins));
              if (soundEnabledRef.current) playExplosionSound();
              setTimeout(() => { router.push('/game-over-screen'); }, 1400);
              break;
            }
          }
        }

        // ── Boss trigger ──────────────────────────────────────────────────────
        if (!startFromPlanet && state === 'playing' && !bossActiveRef.current && levelFrameRef.current >= LEVEL_DURATION_FRAMES) {
          bossActiveRef.current = true;
          asteroidsRef.current = [];
          bossRef.current = createBossAsteroid(cw, level);
          gameStateRef.current = 'boss';
          setGamePhase('boss');
          setBossHp(level.bossHp);
          setBossMaxHp(level.bossHp);
          setShowBossWarning(true);
          setTimeout(() => setShowBossWarning(false), 2500);
        }

        // ── BOSS PHASE ────────────────────────────────────────────────────────
        if (state === 'boss' && bossRef.current) {
          const boss = bossRef.current;

          // Boss movement
          if (boss.phase === 'entering') {
            boss.y += 2.5;
            if (boss.y >= boss.targetY) {
              boss.y = boss.targetY;
              boss.phase = 'fighting';
            }
          } else if (boss.phase === 'fighting') {
            // Side-to-side patrol
            boss.vx = Math.sin(frameCountRef.current * 0.018) * 2.5;
            boss.x += boss.vx;
            boss.x = Math.max(boss.radius + 20, Math.min(cw - boss.radius - 20, boss.x));
            // Shake when low hp
            if (boss.hp <= boss.maxHp * 0.3) {
              boss.shakeX = (Math.random() - 0.5) * 4;
              boss.shakeY = (Math.random() - 0.5) * 4;
            } else {
              boss.shakeX = 0;
              boss.shakeY = 0;
            }

            // UFO shoots at player every ~90 frames (faster when low hp)
            const shootInterval = boss.hp / boss.maxHp < 0.4 ? 50 : 90;
            if (frameCountRef.current % shootInterval === 0) {
              const bx = boss.x + boss.shakeX;
              const by = boss.y + boss.shakeY + boss.radius * 0.3;
              const dx = ship.x - bx;
              const dy = ship.y - by;
              const dist = Math.hypot(dx, dy) || 1;
              const speed = 5;
              enemyBulletsRef.current.push({
                id: `ebullet-${frameCountRef.current}-${Math.floor(Math.random() * 9999)}`,
                x: bx,
                y: by,
                vx: (dx / dist) * speed,
                vy: (dy / dist) * speed,
              });
              // Spread shot when low hp
              if (boss.hp / boss.maxHp < 0.5) {
                const spreadAngles = [-0.25, 0.25];
                spreadAngles.forEach((offset, si) => {
                  const angle = Math.atan2(dy, dx) + offset;
                  enemyBulletsRef.current.push({
                    id: `ebullet-spread-${frameCountRef.current}-${si}`,
                    x: bx,
                    y: by,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                  });
                });
              }
            }
          } else if (boss.phase === 'dying') {
            // Handled below
          }

          boss.rotationAngle += boss.rotationSpeed;
          if (boss.hitFlash > 0) boss.hitFlash--;

          // Enemy bullet collision with player
          const shipCollisionRadius = ship.width * 0.38;
          const hitByEnemyBullet = enemyBulletsRef.current.some((eb) => {
            const dx = eb.x - ship.x;
            const dy = eb.y - (ship.y - ship.height * 0.1);
            return Math.sqrt(dx * dx + dy * dy) < shipCollisionRadius + 6;
          });
          if (hitByEnemyBullet && boss.phase === 'fighting') {
            particlesRef.current.push(...createExplosionParticles(ship.x, ship.y, frameCountRef.current));
            gameStateRef.current = 'dead';
            setGamePhase('dead');
            const finalScore = scoreRef.current;
            const stored = localStorage.getItem('spaceracer-highscore');
            const prev = stored ? parseInt(stored, 10) : 0;
            if (finalScore > prev) localStorage.setItem('spaceracer-highscore', String(finalScore));
            localStorage.setItem('spaceracer-lastscore', String(finalScore));
            localStorage.setItem('spaceracer-lastspeed', String(speedTierRef.current));
            const earnedCoins = Math.floor(finalScore / 10);
            const prevCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
            localStorage.setItem('spaceracer-coins', String(prevCoins + earnedCoins));
            if (soundEnabledRef.current) playExplosionSound();
            setTimeout(() => { router.push('/game-over-screen'); }, 1400);
          }

          // Bullet-boss collision
          const bulletsToRemove = new Set<string>();
          for (const bullet of bulletsRef.current) {
            const dx = bullet.x - (boss.x + boss.shakeX);
            const dy = bullet.y - (boss.y + boss.shakeY);
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < boss.radius + 4) {
              bulletsToRemove.add(bullet.id);
              boss.hp = Math.max(0, boss.hp - 1);
              boss.hitFlash = 8;
              setBossHp(boss.hp);
              particlesRef.current.push(...createAsteroidHitParticles(boss.x, boss.y, boss.color, frameCountRef.current));
              if (soundEnabledRef.current) playBossHitSound();

              // Score for hitting boss
              const pts = 50;
              scoreRef.current += pts;
              setHudScore(scoreRef.current);
              scorePopupsRef.current.push({
                id: `popup-boss-${frameCountRef.current}`,
                x: boss.x,
                y: boss.y,
                vy: -2.5,
                text: `+${pts}`,
                life: 1.0,
              });

              if (boss.hp <= 0) {
                // Boss destroyed!
                boss.phase = 'dying';
                particlesRef.current.push(...createExplosionParticles(boss.x, boss.y, frameCountRef.current, true));
                fragmentsRef.current.push(...createFragments(boss, frameCountRef.current, 20));
                if (soundEnabledRef.current) playBossDeathSound();

                // Trophy: boss slayer
                bossesDefeatedRef.current++;
                if (bossesDefeatedRef.current === 1) tryAwardTrophy('boss_slayer');

                // Big score bonus
                const bonusPts = level.bossHp * 100;
                scoreRef.current += bonusPts;
                setHudScore(scoreRef.current);
                scorePopupsRef.current.push({
                  id: `popup-boss-kill-${frameCountRef.current}`,
                  x: boss.x,
                  y: boss.y,
                  vy: -2.5,
                  text: `+${bonusPts} BOSS!`,
                  life: 2.0,
                });

                // Trigger level complete
                levelCompleteTimerRef.current = 120; // 2 seconds
                gameStateRef.current = 'level_complete';
                setGamePhase('level_complete');
                if (soundEnabledRef.current) playLevelCompleteSound();
                bossRef.current = null;
                bossActiveRef.current = false;
                setBossHp(0);
                setBossMaxHp(0);
              }
              break;
            }
          }
          if (bulletsToRemove.size > 0) {
            bulletsRef.current = bulletsRef.current.filter((b) => !bulletsToRemove.has(b.id));
          }

          // Boss collision with player
          if (boss.phase === 'fighting') {
            const shipCollisionRadius = ship.width * 0.38;
            if (circlesOverlap(ship.x, ship.y - ship.height * 0.1, shipCollisionRadius, boss.x + boss.shakeX, boss.y + boss.shakeY, boss.radius * 0.82)) {
              particlesRef.current.push(...createExplosionParticles(ship.x, ship.y, frameCountRef.current));
              gameStateRef.current = 'dead';
              setGamePhase('dead');
              const finalScore = scoreRef.current;
              const stored = localStorage.getItem('spaceracer-highscore');
              const prev = stored ? parseInt(stored, 10) : 0;
              if (finalScore > prev) localStorage.setItem('spaceracer-highscore', String(finalScore));
              localStorage.setItem('spaceracer-lastscore', String(finalScore));
              localStorage.setItem('spaceracer-lastspeed', String(speedTierRef.current));
              // Save coins (1 coin per 10 points)
              const earnedCoins = Math.floor(finalScore / 10);
              const prevCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
              localStorage.setItem('spaceracer-coins', String(prevCoins + earnedCoins));
              if (soundEnabledRef.current) playExplosionSound();
              setTimeout(() => { router.push('/game-over-screen'); }, 1400);
            }
          }
        }
      } // end if (state === 'playing' || state === 'boss')

      // ── Level complete transition ──────────────────────────────────────────
      if (gameStateRef.current === 'level_complete') {
        levelCompleteTimerRef.current--;
        if (levelCompleteTimerRef.current <= 0) {
          const nextLevelIdx = currentLevelRef.current + 1;

          // Level completion trophies
          if (nextLevelIdx >= 1) tryAwardTrophy('level_2');
          if (nextLevelIdx >= 2) tryAwardTrophy('level_3');
          if (nextLevelIdx >= 4) tryAwardTrophy('level_5');
          if (nextLevelIdx >= 7) tryAwardTrophy('level_8');

          if (nextLevelIdx >= TOTAL_LEVELS) {
            // All levels complete — go to game over with victory
            tryAwardTrophy('level_10');
            const finalScore = scoreRef.current;
            const stored = localStorage.getItem('spaceracer-highscore');
            const prev = stored ? parseInt(stored, 10) : 0;
            if (finalScore > prev) localStorage.setItem('spaceracer-highscore', String(finalScore));
            localStorage.setItem('spaceracer-lastscore', String(finalScore));
            localStorage.setItem('spaceracer-lastspeed', String(speedTierRef.current));
            localStorage.setItem('spaceracer-victory', '1');
            // Save coins (1 coin per 10 points)
            const earnedCoins = Math.floor(finalScore / 10);
            const prevCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
            localStorage.setItem('spaceracer-coins', String(prevCoins + earnedCoins));
            router.push('/game-over-screen');
          } else {
            // Advance to next level
            currentLevelRef.current = nextLevelIdx;
            levelFrameRef.current = 0;
            bossActiveRef.current = false;
            bossRef.current = null;
            asteroidsRef.current = [];
            fragmentsRef.current = [];
            bulletsRef.current = [];
            enemyBulletsRef.current = [];
            particlesRef.current = [];
            scorePopupsRef.current = [];
            scoreRef.current = 0;
            scrollSpeedRef.current = LEVELS[nextLevelIdx].scrollSpeed;
            gameStateRef.current = 'playing';
            setGamePhase('playing');
            setHudLevel(nextLevelIdx + 1);
            setLevelTransitionText(LEVELS[nextLevelIdx].name);
            setTimeout(() => setLevelTransitionText(''), 2500);
          }
        }
      }
    }

    // ── Draw asteroids ────────────────────────────────────────────────────
    if (state !== 'start') {
      asteroidsRef.current.forEach((a) => drawAsteroid(ctx, a));
    }

    // ── Draw free-fly planet (centered) ──────────────────────────────────
    if (startFromPlanet && state !== 'start') {
      const planetRadius = Math.min(cw, ch) * 0.13;
      drawFreeFlyPlanet(ctx, cw / 2, ch / 2, planetRadius, planetParam || '', frameCountRef.current);
    }

    // ── Draw boss ─────────────────────────────────────────────────────────
    if ((state === 'boss' || state === 'level_complete') && bossRef.current) {
      drawAlienUFO(ctx, bossRef.current, frameCountRef.current);
    }

    // ── Draw enemy bullets ────────────────────────────────────────────────
    if (state === 'boss') {
      enemyBulletsRef.current.forEach((eb) => drawEnemyBullet(ctx, eb));
    }

    // ── Draw fragments ────────────────────────────────────────────────────
    if (state !== 'start') {
      fragmentsRef.current = fragmentsRef.current.filter((f) => {
        f.x += f.vx;
        f.y += f.vy;
        f.vy += 0.07;
        f.vx *= 0.98;
        f.rotationAngle += f.rotationSpeed;
        f.life -= 0.018;
        return f.life > 0;
      });
      fragmentsRef.current.forEach((f) => drawFragment(ctx, f));
    }

    // ── Draw bullets ──────────────────────────────────────────────────────
    if (state === 'playing' || state === 'boss') {
      const bCfg = getBlasterConfig(selectedBlasterRef.current);
      bulletsRef.current.forEach((b) => drawBullet(ctx, b, bCfg.color, bCfg.glowColor));
    }

    // ── Draw particles ────────────────────────────────────────────────────
    particlesRef.current = particlesRef.current.filter((p) => p.life > 0);
    particlesRef.current.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08;
      p.vx *= 0.98;
      p.life -= 0.025 / p.maxLife;
    });
    drawParticles(ctx, particlesRef.current);

    // ── Animate & draw score popups ───────────────────────────────────────
    if (state === 'playing' || state === 'boss' || state === 'level_complete') {
      scorePopupsRef.current = scorePopupsRef.current.filter((p) => {
        p.y += p.vy;
        p.life -= 0.022;
        return p.life > 0;
      });
      drawScorePopups(ctx, scorePopupsRef.current);
    }

    // ── Draw ship ─────────────────────────────────────────────────────────
    if (state === 'playing' || state === 'paused' || state === 'boss') {
      drawSpaceship(ctx, shipRef.current, frameCountRef.current, getShipConfig(selectedShipRef.current));
    }

    animFrameRef.current = requestAnimationFrame(gameLoop);
  }, [router, tryAwardTrophy]);

  // ─── Setup ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      canvasSizeRef.current = { width: window.innerWidth, height: window.innerHeight };
      starsRef.current = createStars(window.innerWidth, window.innerHeight);
      if (gameStateRef.current === 'start') {
        shipRef.current.x = window.innerWidth / 2;
        shipRef.current.y = window.innerHeight * SHIP_Y_RATIO;
        shipRef.current.targetX = window.innerWidth / 2;
      }
    };

    resize();
    window.addEventListener('resize', resize);

    starsRef.current = createStars(canvas.width, canvas.height);
    shipRef.current = {
      x: canvas.width / 2,
      y: canvas.height * SHIP_Y_RATIO,
      width: SHIP_WIDTH,
      height: SHIP_HEIGHT,
      targetX: canvas.width / 2,
      engineFlicker: 0,
      rotation: 0,
    };

    // Auto-start if coming from galaxy map
    if (startFromPlanet) {
      initGame(canvas.width, canvas.height);
    }

    animFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animFrameRef.current);
      stopAmbience();
    };
  }, [gameLoop]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      keysRef.current[e.key] = true;
      if ((e.key === 'Enter') && gameStateRef.current === 'start') {
        const canvas = canvasRef.current;
        if (canvas) {
          if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
          initGame(canvas.width, canvas.height);
        }
      }
      if (e.key === 'Escape') {
        if (gameStateRef.current === 'playing' || gameStateRef.current === 'boss') {
          gameStateRef.current = 'paused';
          setGamePhase('paused');
          if (ambienceGain && audioCtx) {
            ambienceGain.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
          }
        } else if (gameStateRef.current === 'paused' || gameStateRef.current === 'playing' || gameStateRef.current === 'boss') {
          const prevState = bossActiveRef.current ? 'boss' : 'playing';
          gameStateRef.current = prevState;
          setGamePhase(prevState);
          if (soundEnabledRef.current && ambienceGain && audioCtx) {
            ambienceGain.gain.linearRampToValueAtTime(0.06, audioCtx.currentTime + 0.3);
          }
        }
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false;
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [initGame]);

  const touchStartXRef = useRef<number | null>(null);
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartXRef.current = e.touches[0].clientX;
      if ((gameStateRef.current === 'playing' || gameStateRef.current === 'boss') && bulletCooldownRef.current === 0) {
        bulletsRef.current.push(createBullet(shipRef.current, frameCountRef.current));
        bulletCooldownRef.current = BULLET_COOLDOWN;
        if (soundEnabledRef.current) playLaserSound();
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartXRef.current === null) return;
      const dx = e.touches[0].clientX - touchStartXRef.current;
      if (dx < -10) {
        keysRef.current['ArrowLeft'] = true;
        keysRef.current['ArrowRight'] = false;
      } else if (dx > 10) {
        keysRef.current['ArrowRight'] = true;
        keysRef.current['ArrowLeft'] = false;
      }
    };
    const handleTouchEnd = () => {
      keysRef.current['ArrowLeft'] = false;
      keysRef.current['ArrowRight'] = false;
      touchStartXRef.current = null;
    };
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleStartGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
      initGame(canvas.width, canvas.height);
    }
  }, [initGame]);

  const handleToggleSound = useCallback(() => {
    const next = !soundEnabledRef.current;
    soundEnabledRef.current = next;
    setSoundEnabled(next);
    if (!next) {
      stopAmbience();
    } else if (gameStateRef.current === 'paused' || gameStateRef.current === 'playing' || gameStateRef.current === 'boss') {
      startAmbience();
    }
  }, []);

  const handleDifficultyChange = useCallback((d: 'easy' | 'normal' | 'hard') => {
    difficultyRef.current = d;
    setDifficulty(d);
  }, []);

  const handleBackToMenu = useCallback(() => {
    stopAmbience();
    gameStateRef.current = 'start';
    setGamePhase('start');
    asteroidsRef.current = [];
    fragmentsRef.current = [];
    bulletsRef.current = [];
    enemyBulletsRef.current = [];
    particlesRef.current = [];
    scorePopupsRef.current = [];
    bossRef.current = null;
    bossActiveRef.current = false;
    scoreRef.current = 0;
    scrollSpeedRef.current = BASE_SCROLL_SPEED;
    speedTierRef.current = 1;
    currentLevelRef.current = 0;
    levelFrameRef.current = 0;
    setHudScore(0);
    setHudSpeed(1);
    setHudLevel(1);
    setBossHp(0);
    setBossMaxHp(0);
    setShowBossWarning(false);
  }, []);

  const currentLevelConfig = LEVELS[Math.min(hudLevel - 1, LEVELS.length - 1)];

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black scanline-overlay">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* HUD overlay */}
      {(gamePhase === 'playing' || gamePhase === 'boss') && (
        <HUD score={hudScore} speedTier={hudSpeed} />
      )}

      {/* Level indicator */}
      {(gamePhase === 'playing' || gamePhase === 'boss') && (
        <div
          className="absolute top-5 right-5 font-mono text-xs tracking-widest uppercase px-3 py-1.5 rounded"
          style={{
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          {currentLevelConfig?.label ?? `BANE ${hudLevel}`}
        </div>
      )}

      {/* Boss health bar */}
      {gamePhase === 'boss' && bossMaxHp > 0 && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1" style={{ minWidth: 260 }}>
          <div className="font-mono text-xs tracking-widest uppercase" style={{ color: '#00ff88', textShadow: '0 0 8px #00ff88' }}>
            ⚠ ALIEN UFO ⚠
          </div>
          <div className="w-64 h-4 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(0,255,100,0.5)' }}>
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{
                width: `${(bossHp / bossMaxHp) * 100}%`,
                background: bossHp / bossMaxHp > 0.5
                  ? 'linear-gradient(90deg, #00cc55, #00ff88)'
                  : 'linear-gradient(90deg, #cc0000, #ff4400)',
                boxShadow: '0 0 8px rgba(0,255,100,0.8)',
              }}
            />
          </div>
          <div className="font-mono text-xs" style={{ color: 'rgba(0,255,140,0.8)' }}>
            {bossHp} / {bossMaxHp} HP
          </div>
        </div>
      )}

      {/* Boss warning */}
      {showBossWarning && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ animation: 'pulse-glow 0.5s ease-in-out 3' }}
        >
          <div
            className="font-mono text-center px-8 py-6 rounded-lg"
            style={{
              background: 'rgba(0,0,0,0.85)',
              border: '2px solid rgba(0,255,100,0.8)',
              boxShadow: '0 0 40px rgba(0,255,100,0.4)',
            }}
          >
            <div className="text-3xl font-bold tracking-widest mb-2" style={{ color: '#00ff88', textShadow: '0 0 20px #00ff88' }}>
              ⚠ ALIEN INVASION ⚠
            </div>
            <div className="text-sm tracking-widest uppercase" style={{ color: 'rgba(0,255,140,0.9)' }}>
              En alien UFO nærmer sig!
            </div>
          </div>
        </div>
      )}

      {/* Level transition banner */}
      {levelTransitionText && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ animation: 'fadeInOut 2.5s ease-in-out forwards' }}
        >
          <div
            className="font-mono text-center px-10 py-8 rounded-lg"
            style={{
              background: 'rgba(0,0,0,0.9)',
              border: '2px solid rgba(0,229,255,0.5)',
              boxShadow: '0 0 40px rgba(0,229,255,0.2)',
            }}
          >
            <div className="text-sm tracking-widest uppercase mb-2" style={{ color: 'rgba(0,229,255,0.7)' }}>
              BANE GENNEMFØRT!
            </div>
            <div className="text-3xl font-bold tracking-widest" style={{ color: '#00e5ff', textShadow: '0 0 20px #00e5ff' }}>
              {levelTransitionText}
            </div>
          </div>
        </div>
      )}

      {/* Trophy popup */}
      {trophyPopup && (
        <div
          className="absolute left-1/2 pointer-events-none"
          style={{
            top: '18%',
            transform: 'translateX(-50%)',
            opacity: trophyPopup.visible ? 1 : 0,
            transition: 'opacity 0.4s ease',
            zIndex: 60,
          }}
        >
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-xl"
            style={{
              background: 'rgba(0,0,0,0.92)',
              border: `2px solid ${trophyPopup.trophy.color}`,
              boxShadow: `0 0 24px ${trophyPopup.trophy.color}55, 0 0 60px ${trophyPopup.trophy.color}22`,
              minWidth: 260,
            }}
          >
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>{trophyPopup.trophy.icon}</span>
            <div className="flex flex-col">
              <span
                className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold"
                style={{ color: trophyPopup.trophy.color, textShadow: `0 0 8px ${trophyPopup.trophy.color}` }}
              >
                POKAL OPTJENT!
              </span>
              <span
                className="font-mono text-sm font-bold tracking-wider uppercase"
                style={{ color: '#ffffff' }}
              >
                {trophyPopup.trophy.name}
              </span>
              <span
                className="font-mono text-[10px] tracking-wide mt-0.5"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                {trophyPopup.trophy.description}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Pause button */}
      {(gamePhase === 'playing' || gamePhase === 'boss') && (
        <button
          onClick={pauseGame}
          className="absolute top-5 left-1/2 -translate-x-1/2 font-mono-game text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded pointer-events-auto transition-opacity duration-150 hover:opacity-100"
          style={{
            background: 'rgba(0,229,255,0.07)',
            border: '1px solid rgba(0,229,255,0.2)',
            color: 'rgba(0,229,255,0.45)',
            opacity: 0.6,
          }}
        >
          ⏏ PAUSE
        </button>
      )}

      {/* Start screen overlay */}
      {gamePhase === 'start' && (
        <StartScreen onStart={handleStartGame} />
      )}

      {/* Pause menu overlay */}
      {gamePhase === 'paused' && (
        <PauseMenu
          onResume={resumeGame}
          onBackToMenu={handleBackToMenu}
          soundEnabled={soundEnabled}
          onToggleSound={handleToggleSound}
          difficulty={difficulty}
          onDifficultyChange={handleDifficultyChange}
        />
      )}

      {/* Death flash */}
      {gamePhase === 'dead' && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(255,23,68,0.35) 0%, rgba(255,23,68,0.05) 60%, transparent 100%)',
            animation: 'pulse-glow 0.3s ease-in-out 2',
          }}
        />
      )}

      {/* Shoot hint */}
      {(gamePhase === 'playing' || gamePhase === 'boss') && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-cyan-400/40 pointer-events-none select-none">
          SPACE — skyd
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: scale(0.9); }
          15% { opacity: 1; transform: scale(1); }
          75% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}